
import {SyncedField} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/syncedfield';
import {SyncedFieldSetting} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/syncedfieldsetting';
import {ContentMappedFieldMenu} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/syncedfieldstable/contentmappedfieldmenu';
import {OverwriteDataToggle} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/syncedfieldstable/overwritedatatoggle';
import {SyncToggle} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/syncedfieldstable/synctoggle';
import {CalloutBanner, CalloutBannerType} from '/ui/components/calloutbanner/calloutbanner';
import {Table} from '/ui/components/table/table';
import {Tooltip} from '/ui/components/tooltip/tooltip';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/syncedfieldstable/syncedfieldstable.module.scss';

export function SyncedFieldsTable({syncedFields, syncedFieldSettings, setSyncedFieldSettings}) {
  return (
    <>
      <div className={styles.warningCallout}>
        <CalloutBanner type={CalloutBannerType.WARNING} title={undefined} children={undefined}>
          {yext.msg('Any changes from the default settings will overwrite the default account-level settings.')}
        </CalloutBanner>
      </div>
      <Table
        label={yext.msg('Table of synced fields and their settings')}
        columns={getColumns(syncedFields)}
        dataRows={getSyncedFieldRows(syncedFields, syncedFieldSettings, setSyncedFieldSettings)}
      />
    </>
  );
}

const COLUMN_IDS = {
  FIELD: 'field',
  SYNC: 'sync',
  OVERWRITE_DATA: 'overwriteData',
  CONTENT_MAPPED_FIELD: 'contentMappedField',
};

/**
 *
 * @param {!Array<SyncedField>} syncedFields
 * @returns {!Array<?>} Array of Table.Column
 */
function getColumns(syncedFields) {
  const baseColumns = [
    {
      id: COLUMN_IDS.FIELD,
      label: yext.msg('Field'),
    },
    {
      id: COLUMN_IDS.SYNC,
      label: yext.msg('Sync'),
    },
  ];

  const overwriteColumn = {
    id: COLUMN_IDS.OVERWRITE_DATA,
    label: <Tooltip
      tooltipText={yext.msg('Overwriting data will replace existing content on your listings with content from your entities.')}
      unclearText={yext.msg('Overwrite Data')}
    />,
  };

  const mappingColumn = {
    id: COLUMN_IDS.CONTENT_MAPPED_FIELD,
    label: yext.msg('Mapped Field'),
  };

  const someCanOverwrite = syncedFields.some(f => f.canOverwriteData);
  if (someCanOverwrite) {
    return [...baseColumns, overwriteColumn, mappingColumn];
  } else {
    return [...baseColumns, mappingColumn];
  }
}

function getSyncedFieldRows(syncedFields, syncedFieldSettings, setSyncedFieldSettings) {
  return syncedFields.map(syncedField => {
    let syncedFieldSetting = findSetting(syncedField, syncedFieldSettings);

    if (syncedFieldSetting === undefined) {
      syncedFieldSetting = new SyncedFieldSetting({
        syncedFieldKey: syncedField.key,
        overrideFieldKey: undefined,
        enabled: true,
        replaceExisting: true,
        applyToNewSubAccounts: false,
      });
    }

    const setSyncedFieldSetting = newSetting => {
      if (findSetting(syncedField, syncedFieldSettings)) {
        setSyncedFieldSettings(
          syncedFieldSettings.map(setting =>
            newSetting.syncedFieldKey === setting.syncedFieldKey ? newSetting : setting));
      } else {
        setSyncedFieldSettings([...syncedFieldSettings, newSetting]);
      }
    };

    return getSyncedFieldRow(syncedField, syncedFieldSetting, setSyncedFieldSetting);
  }).sort((row1, row2) => row1.order - row2.order);
}

/**
 * @param {SyncedField} syncedField
 * @param {!Array<SyncedFieldSetting>} syncedFieldSettings
 * @returns {SyncedFieldSetting | undefined}
 */
function findSetting(syncedField, syncedFieldSettings) {
  return syncedFieldSettings.find(setting =>
    isMatch(syncedField, setting));
}

/**
 * @param {SyncedField} syncedField
 * @param {SyncedFieldSetting} syncedFieldSetting
 */
function isMatch(syncedField, syncedFieldSetting) {
  return syncedField.key.id === syncedFieldSetting.syncedFieldKey.id
    && syncedField.key.typeId === syncedFieldSetting.syncedFieldKey.typeId;
}

/**
 * @param {SyncedField} syncedField
 * @param {SyncedFieldSetting} syncedFieldSetting
 * @param {Function} setSyncedFieldSetting
 * @returns
 */
function getSyncedFieldRow(syncedField, syncedFieldSetting, setSyncedFieldSetting) {
  return {
    id: syncedField.name,
    order: syncedField.order,
    [COLUMN_IDS.FIELD]:
      syncedField.description === undefined
        ? syncedField.name
        : <Tooltip
          tooltipText={syncedField.description}
          unclearText={syncedField.name}
        />,
    [COLUMN_IDS.SYNC]:
      <SyncToggle
        syncedFieldSetting={syncedFieldSetting}
        setSyncedFieldSetting={setSyncedFieldSetting}
      />,
    [COLUMN_IDS.OVERWRITE_DATA]:
      <OverwriteDataToggle
        syncedField={syncedField}
        syncedFieldSetting={syncedFieldSetting}
        setSyncedFieldSetting={setSyncedFieldSetting}
      />,
    [COLUMN_IDS.CONTENT_MAPPED_FIELD]:
      <ContentMappedFieldMenu
        syncedField={syncedField}
        syncedFieldSetting={syncedFieldSetting}
        setSyncedFieldSetting={setSyncedFieldSetting}
      />,
  };
}
